import { Injectable } from '@angular/core';
import {
  Router,
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { ApiService } from '../services/api.service';
import { NotificationService } from '../services/notification.service';
import { I18nService } from '../services/i18n.service';
import { PRE_ATTENDANCE_PERMISSION } from '../utilities/backend-menu.util';

/**
 * Smart pre-attendance is only for whoever has attendance released: the same access key gates the
 * `/api/pre-attendance` endpoints and the sidebar item, so a user without it never sees the entry,
 * cannot reach the route by typing it, and is refused by the API — one key, three layers.
 */
@Injectable({
  providedIn: 'root'
})
export class PreAttendanceGuard implements CanActivate {

  constructor(
    private router: Router,
    private apiService: ApiService,
    private notificationService: NotificationService,
    private i18n: I18nService
  ) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.apiService
      .get<{ canAccess: boolean }>('user-access/check', { permission: PRE_ATTENDANCE_PERMISSION })
      .pipe(
        map(res => res.canAccess),
        tap(canAccess => {
          if (!canAccess) {
            this.denyAccess();
          }
        }),
        catchError(() => {
          this.denyAccess();
          return of(false);
        })
      );
  }

  private denyAccess(): void {
    this.notificationService.warning(
      this.i18n.translate('preAttendance.errors.accessDenied'),
      this.i18n.translate('notifications.accessDenied')
    );

    const currentUrl = this.router.url;
    if (!currentUrl || currentUrl === '/') {
      this.router.navigate(['/home']);
    }
  }
}
